/**
 * Try to extract { lat, lng } from a Google Maps URL or pasted text.
 * Short goo.gl / maps.app.goo.gl links usually cannot be resolved without a server redirect.
 */
export function parseLatLngFromGoogleMapsUrl(text) {
  if (!text || typeof text !== 'string') return null;
  const s = text.trim();
  if (!s) return null;

  // Precise place pin (prefer over @ viewport center in long /place/... URLs)
  const d34 = s.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
  if (d34) {
    const lat = Number(d34[1]);
    const lng = Number(d34[2]);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
  }

  // @lat,lng,zoom (viewport — may differ slightly from !3d pin)
  const at = s.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)(?:,|\s|$)/);
  if (at) {
    const lat = Number(at[1]);
    const lng = Number(at[2]);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
  }

  // ?q=lat,lng or &q=lat,lng
  const q = s.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (q) {
    const lat = Number(q[1]);
    const lng = Number(q[2]);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
  }

  // ll=lat,lng
  const ll = s.match(/[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (ll) {
    const lat = Number(ll[1]);
    const lng = Number(ll[2]);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
  }

  // center=lat%2Clng or center=lat,lng
  const center = s.match(/[?&]center=(-?\d+\.?\d*)[,%2C](-?\d+\.?\d*)/i);
  if (center) {
    const lat = Number(center[1]);
    const lng = Number(center[2]);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
  }

  // ?q=12.34%2C77.56 or ?q=12.34, 77.56 (decoded)
  const qAny = s.match(/[?&]q=([^&]+)/);
  if (qAny) {
    try {
      const dec = decodeURIComponent(qAny[1].replace(/\+/g, ' '));
      const m = dec.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
      if (m) {
        const lat = Number(m[1]);
        const lng = Number(m[2]);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
      }
    } catch {
      /* ignore */
    }
  }

  return null;
}

/**
 * Google `/place/Locality,+City,+State/` — use city/locality when reverse-geocode has no `city`.
 */
export function extractPlacePathHints(text) {
  if (!text || typeof text !== 'string') return {};
  const m = text.match(/\/place\/([^/@?]+)/i);
  if (!m) return {};
  let raw;
  try {
    raw = decodeURIComponent(m[1].replace(/\+/g, ' '));
  } catch {
    return {};
  }
  const parts = raw
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 3) {
    return {
      placePathLocality: parts[0],
      placePathCity: parts[parts.length - 2],
      placePathState: parts[parts.length - 1],
    };
  }
  if (parts.length === 2) {
    return { placePathCity: parts[0], placePathState: parts[1] };
  }
  if (parts.length === 1) {
    return { placePathCity: parts[0] };
  }
  return {};
}

/**
 * Extract a human place / address string from URLs that do not expose coordinates.
 */
function extractPlaceQueryFromUrl(s) {
  const queryParam = s.match(/[?&]query=([^&]+)/i);
  if (queryParam) {
    try {
      const raw = decodeURIComponent(queryParam[1].replace(/\+/g, ' ')).trim();
      if (raw.length >= 2) return raw;
    } catch {
      /* ignore */
    }
  }

  const qParam = s.match(/[?&]q=([^&]+)/);
  if (qParam) {
    try {
      const raw = decodeURIComponent(qParam[1].replace(/\+/g, ' ')).trim();
      if (raw.length < 2) return null;
      const coordPair = /^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/.exec(raw);
      if (coordPair) {
        const lat = Number(coordPair[1]);
        const lng = Number(coordPair[2]);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) return null;
      }
      return raw;
    } catch {
      return null;
    }
  }

  const placePath = s.match(/\/place\/([^/@?]+)/i);
  if (placePath) {
    try {
      const raw = decodeURIComponent(placePath[1].replace(/\+/g, ' ')).trim();
      if (raw.length >= 2) return raw;
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Coordinates from the URL, or a place query to search (same resolution as map search).
 * Returns { lat, lng } | { placeQuery: string } | null
 */
export function parseGoogleMapsUrl(text) {
  const hints = extractPlacePathHints(text);
  const coords = parseLatLngFromGoogleMapsUrl(text);
  if (coords) return { ...coords, ...hints };

  if (!text || typeof text !== 'string') return null;
  const s = text.trim();
  if (!s) return null;

  const placeQuery = extractPlaceQueryFromUrl(s);
  if (placeQuery) return { placeQuery, ...hints };
  return null;
}
